import { FC, useEffect, useState, ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';

interface TableProps {
  classNameMain?: string;
  tableData?: any[];
  classNameBody?: string;
  tableColumns?: any[];
  classNameHead?: string;
  className?: string;
  initialState?: any;
  classNameTd?: string;
  children?: ReactNode;
  classNameTBody?: string;
  onRowClick?: (row: any) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

const Table: FC<TableProps> = ({
  classNameMain,
  tableData,
  classNameBody,
  tableColumns,
  classNameHead,
  className,
  initialState,
  classNameTd,
  children,
  classNameTBody,
  onRowClick,
  emptyStateTitle,
  emptyStateDescription
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>(tableData ?? []);
  const [columns, setColumns] = useState<any[]>(tableColumns ?? []);

  useEffect(() => {
    setData(tableData ?? []);
    setColumns(tableColumns ?? []);
  }, [tableData, tableColumns]);

  const tableInstance = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState
  });

  // Check if table is empty
  const isEmpty = data && data.length === 0;

  if (isEmpty) {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 ${classNameMain || ''}`}>
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="flex justify-center mb-4">
            <SVGAssets.FilterEmpty className="w-24 h-24 opacity-60" />
          </div>
          <h3 className="text-base font-semibold text-[#353535] mb-2">
            {emptyStateTitle || t('common.noDataFound', 'No data found')}
          </h3>
          <p className="text-sm text-[#505050] text-center max-w-xs">
            {emptyStateDescription ||
              t(
                'common.tryAdjustingFilters',
                'Try adjusting your filters or search criteria'
              )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 ${classNameMain || ''}`}>
      <div className="w-full overflow-x-auto">
        <table className={`w-full min-w-max ${className || ''}`}>
          <thead className="bg-gray-50 border-b border-gray-200">
            {tableInstance.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={`px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider ${classNameHead || ''}`}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            className={`bg-white divide-y divide-gray-200 ${classNameTBody || ''}`}>
            {tableInstance.getRowModel().rows.map(row => {
              return (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={`hover:bg-gray-100 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${classNameBody || ''}`}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`px-3 py-2 whitespace-nowrap text-sm text-gray-900 ${classNameTd || ''}`}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
            {children}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export { Table };
